// Checkout Forms
import { setFieldValue } from 'checkout-forms/redux/actions/form/formActions';

// Types
import { ProductProps } from 'types/Product.types';
import { AppDispatch, AppClient, AppGetState } from 'types/RootState.types';
import { ShowItemRemoveOverlayTypes } from './editBasketActions.types';

// Config
import { navigateTo, navigateReplace } from '../navigation/navigationActions';
import {
  ANALYTICS_EDIT_BASKET_MODAL_OPEN,
  SET_EDIT_BASKET_BASE_ROUTE,
  REBATCH_ORDER,
  PUT_ITEM_QUANTITY,
  PUT_ITEMS_QUANTITIES,
  ANALYTICS_AMEND_BASKET_MODAL_OPEN,
  SHOW_ITEM_REMOVE_OVERLAY,
  HANDLE_EDIT_BASKET_QUANTITY_ERROR,
  HIDE_ITEM_REMOVE_OVERLAY,
  REMOVE_BASKET_ITEMS,
  GET_ITEMS,
  BASKET_ITEM_OUT_OF_STOCK,
  SHOW_BATCHING_FAILURE_MODAL,
  ANALYTICS_BATCH_ERROR,
  ANALYTICS_BATCH_ERROR_ITEMS_REMOVED,
  SHOW_REMOVE_BATCHING_FAILURE_ERROR,
  ANALYTICS_POS_CREDIT_INELIGIBLE_ITEMS_REMOVED,
  ANALYTICS_SHOW_COLLECTION_HELP_REMOVE,
  CLOSE_EDIT_BASKET_MODAL,
  SAVE_ITEM_FOR_LATER,
  HANDLE_UNUSABLE_DELIVERY_ADDRESS_BEFORE_REBATCH,
  SHOW_EDIT_BASKET_MODAL,
} from '@constants/actionConstants';
import {
  URL_PUT_ITEM_QUANTITY,
  URL_PUT_ITEMS_QUANTITIES,
  URL_GET_ITEMS,
  URL_REMOVE_ITEMS_FROM_BASKET,
  URL_REMOVE_TRADE_IN_ITEM,
  URL_SAVE_FOR_LATER,
  URL_DELIVERIES_V4,
} from '@constants/endpointConstants';
import errorCodeConstants from '@constants/errorCodeConstants';
import routeConstants from '@constants/routeConstants';
import { getProcessedHandbackUrl, handbackToUrl } from '@hooks/useHandbackToUrl';
import { triggerAnalyticsEvent } from '@redux/actions/analytics/analyticsAction';
import { showDisableSiteSpinner, showEmptyBasketNotification } from '@redux/actions/app/appActions';
import {
  initDeliveryPage,
  refreshDeliveryMethods,
  setDeliveryAddressAndGetDeliveryMethods,
  setDeliveryAddressOnBasket,
} from '@redux/actions/delivery/deliveryActions';
import { hidePOSCreditBasketAmendModal, postPayments } from '@redux/actions/payment/paymentActions';
import {
  findCollectionPoint,
  selectCollectionPoint,
  getSavedCollectionPoints,
} from '@redux/actions/click-and-collect/clickAndCollectActions';
import { getOrderFormData, getOrderFormCollection } from '@redux/actions/payment/orderFormActions';
import { isApps } from '@redux/reducers/app/appReducer';
import { getIsApplicationSelector } from '@redux/reducers/app/appSelector';
import {
  getCollectionPointSearchTermSelector,
  getSelectedCollectionPointSelector,
} from '@redux/reducers/click-and-collect/clickAndCollectSelector';
import {
  getConfirmedDeliveryAddressSelector,
  getSelectedDeliveryChoiceIdSelector,
} from '@redux/reducers/delivery/deliverySelector';
import {
  getOrderFormIsClickCollectOrderSelector,
  getOrderFormItemsSelector,
  getOrderFormOrderNeedsToBeRebatchedSelector,
  getPaymentPageCompleteSelector,
  getSubmitOrderFailedSelector,
} from '@redux/reducers/order-form/orderFormSelector';
import { getIsExpressPayment } from '@redux/reducers/payment/paymentSelector';
import { getRouterLocationPathname } from '@redux/reducers/router/routerSelector';
import {
  getSelectedDeliveryAddressSelector,
  getUserSavedCollectionPointsSelector,
} from '@redux/reducers/user/userSelector';
import { shouldShowCollectionHelpText } from '@utils/collection/collectionHelp';
import { isClickAndCollectChoice } from '@utils/delivery/deliveryHelper';
import getErrorMessageObject from '@utils/error/getErrorMessageObject';
import { getFormId, ITEM_QUANTITY_INPUT_ID } from '@utils/form/configs/itemQuantity';
import { isPaymentPage, isSecurePage } from '@utils/helpers/pageType';
import { isEmptyObject } from '@utils/object';
import { isWarranty } from '@utils/orderform/getRestrictedBasketFlags';
import isItemFabric, { itemIsM2M } from '@utils/orderform/isItemFabric';
import { shouldRemoveAndSaveForLater } from '@utils/saveForLater/saveForLaterHelp';
import { isSinglePageCheckoutEnabledFeatureActive } from '@redux/reducers/config/configSelector';

export const showEditBasketModal =
  ({ baseRoutePath = '', condition }: { baseRoutePath?: string; condition?: string }) =>
  (dispatch: AppDispatch, getState: AppGetState) => {
    // @TODO: For SPC do we still need a base route?
    dispatch({
      type: SET_EDIT_BASKET_BASE_ROUTE,
      baseRoutePath,
    });

    const payload = condition ? { condition } : undefined;

    const state = getState();
    const singlePageCheckoutEnabled = isSinglePageCheckoutEnabledFeatureActive(state);

    if (singlePageCheckoutEnabled) {
      dispatch({ type: SHOW_EDIT_BASKET_MODAL });
    } else {
      dispatch(navigateTo(routeConstants.EDIT_BASKET));
    }

    dispatch(triggerAnalyticsEvent(ANALYTICS_EDIT_BASKET_MODAL_OPEN, payload));
  };

export const rebatchOrder = (address: any) => async (dispatch: AppDispatch, getState: AppGetState) => {
  const path = URL_DELIVERIES_V4;

  if (getState().user?.selectedDeliveryAddress?.notAvailableForDelivery) {
    // e.g. if customer has tried to use a BFPO address then tries to edit basket
    // this will reset customer's selected address back to something useable
    await dispatch({ type: HANDLE_UNUSABLE_DELIVERY_ADDRESS_BEFORE_REBATCH });
  }

  const rebatchDispatchObject = {
    type: REBATCH_ORDER,
    request: (client: AppClient) =>
      client({
        path,
        config: { method: 'POST' },
      }),
    address,
    orderHasBeenRebatched: true,
  };

  const isClickCollectOrder = getOrderFormIsClickCollectOrderSelector(getState());
  const getPaymentPageComplete = getPaymentPageCompleteSelector(getState());

  // re-confirm collection availability as basket may have changed in or outside of checkout
  dispatch(getOrderFormCollection());

  // APO requires a delivery address set against the order, but basket service strips it off for collection
  // orders, which means the UI must re-set an address in order to successfully rebatch, even though
  // the basket service will strip it off again
  //
  // https://johnlewis.slack.com/archives/G93A6NPFZ/p1752766509710629
  const shouldReSetDeliveryAddress = isClickCollectOrder && getPaymentPageComplete;
  if (shouldReSetDeliveryAddress) await dispatch(setDeliveryAddressOnBasket());

  return dispatch(rebatchDispatchObject);
};

export const rebatchOrderAfterBasketEdit =
  ({ forceGetDeliveryPage = false, shouldInitDeliveryPage = false } = {}) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    const orderNeedsToBeRebatched = getOrderFormOrderNeedsToBeRebatchedSelector(getState());
    const address = getConfirmedDeliveryAddressSelector(getState());
    const selectedDeliveryChoiceId = getSelectedDeliveryChoiceIdSelector(getState());
    const pathname = getRouterLocationPathname();
    const editFromPaymentPage = isPaymentPage({ pathname });
    const editFromSecurePage = isSecurePage({ pathname });

    if (orderNeedsToBeRebatched) {
      dispatch(showDisableSiteSpinner());

      const response = await dispatch(rebatchOrder(address));

      if (response.type === `${REBATCH_ORDER}.SUCCESS`) {
        const hasConfirmedDeliveryAddress = !!getConfirmedDeliveryAddressSelector(getState());
        const selectedDeliveryAddress = getSelectedDeliveryAddressSelector(getState());
        const isClickCollectSelected = isClickAndCollectChoice(selectedDeliveryChoiceId);

        if (isClickCollectSelected) {
          const searchTerm = getCollectionPointSearchTermSelector(getState());
          const selectedCollectionPoint = getSelectedCollectionPointSelector(getState());
          const savedCollectionPoints = getUserSavedCollectionPointsSelector(getState());

          // TODO: Deal with this once click and collect is state based
          if (!editFromSecurePage) {
            dispatch(navigateReplace(routeConstants.CLICK_AND_COLLECT));
          }

          if (!isEmptyObject(savedCollectionPoints)) {
            // refresh saved collection points as collection charges may have changed
            await dispatch(getSavedCollectionPoints());
          }

          if (searchTerm) {
            // refresh collection point search results as collection charges may have changed
            await dispatch(findCollectionPoint(searchTerm, true));
          }

          if (selectedCollectionPoint) {
            // re-select selected collection point to get latest dates & collection charges
            await dispatch(
              selectCollectionPoint({
                collectionPoint: selectedCollectionPoint,
                refresh: true,
              }),
            );
          }
        } else if (hasConfirmedDeliveryAddress) {
          if (editFromPaymentPage && !editFromSecurePage) {
            await dispatch(navigateReplace(routeConstants.DELIVERY));
          } else if (!shouldInitDeliveryPage) {
            await dispatch(refreshDeliveryMethods());
          }
        } else if (selectedDeliveryAddress) {
          await dispatch(
            setDeliveryAddressAndGetDeliveryMethods({
              address: selectedDeliveryAddress,
              // TODO the line below can be removed once deliveryActions is typescripted
              formValues: undefined,
            }),
          );
        }

        if (!editFromPaymentPage && !editFromSecurePage) {
          if (!shouldInitDeliveryPage) {
            const isExpressPayment = getIsExpressPayment(getState());
            const submitOrderFailed = getSubmitOrderFailedSelector(getState());
            const shouldPostPayments = isExpressPayment && submitOrderFailed;

            if (shouldPostPayments) await dispatch(postPayments());
          } else {
            await dispatch(initDeliveryPage({ forceGetDeliveryPage }));
          }
        }
      }
    }
  };

export const showItemRemoveOverlay = ({ isRemove = false, itemId }: ShowItemRemoveOverlayTypes) => ({
  type: SHOW_ITEM_REMOVE_OVERLAY,
  itemId,
  isRemove,
});

export const hideItemRemoveOverlay = (itemId: string) => ({
  type: HIDE_ITEM_REMOVE_OVERLAY,
  itemId,
});

export const getItems = ({ isAgeVerification = false } = {}) => ({
  type: GET_ITEMS,
  isAgeVerification,
  request: (client: AppClient) =>
    client({
      path: URL_GET_ITEMS,
      config: { method: 'GET' },
    }),
});

export const saveItems = (itemIds: any, products: { productId: string }[]) => ({
  type: REMOVE_BASKET_ITEMS,
  actionType: SAVE_ITEM_FOR_LATER,
  itemIds: itemIds.ids,
  products,
  request: (client: AppClient) => client({ path: URL_SAVE_FOR_LATER, config: { method: 'POST', body: itemIds } }),
});

export const removeItems =
  ({
    itemIds = [],
    isTradeIn = false,
    isChildItem = false,
    forceRemove = false,
    rebatchNotNeeded = false,
    isExpressCheckout = false,
  }: {
    forceRemove?: boolean;
    isChildItem?: boolean;
    isExpressCheckout?: boolean;
    isTradeIn?: boolean;
    itemIds: string[];
    rebatchNotNeeded?: boolean;
  }) =>
  (dispatch: AppDispatch, getState: AppGetState) => {
    const state = getState();
    const isSignedIn = state?.user?.isSignedIn ?? false;
    const orderFormItems = state.orderForm?.items ?? [];
    const removableItems = [
      ...orderFormItems,
      ...orderFormItems.flatMap((item: ProductProps) => item.childItems ?? []),
      ...(state.orderForm?.tradeInQuotes ?? []),
    ];
    const products = itemIds.flatMap((itemId) => {
      const productId = removableItems.find((item: ProductProps) => item.id === itemId)?.productId;
      return productId ? [{ productId }] : [];
    });
    const isM2M = orderFormItems.some((item: ProductProps) => itemIds.includes(item.id) && itemIsM2M(item?.type));
    const shouldSaveForLater = shouldRemoveAndSaveForLater({
      isSignedIn,
      isChildItem,
      isTradeIn,
      isM2M,
      isExpressCheckout,
    });

    const itemIdsString = itemIds.toString();
    let path = URL_REMOVE_ITEMS_FROM_BASKET(itemIdsString);

    if (isTradeIn) {
      path = URL_REMOVE_TRADE_IN_ITEM(itemIds);
    }

    if (!shouldSaveForLater || forceRemove) {
      return dispatch({
        type: REMOVE_BASKET_ITEMS,
        request: (client: AppClient) => client({ path, config: { method: 'DELETE' } }),
        itemIds,
        products,
        isTradeIn,
        ...(rebatchNotNeeded && { rebatchNotNeeded }),
      });
    }

    return dispatch(saveItems({ ids: itemIds }, products));
  };

export const handleRemoveItem =
  ({
    itemId = '',
    lastItemInBasket = false,
    isChildItem = false,
    isTradeIn = false,
    isAgeVerification = false,
    isExpressCheckout = false,
    forceRemove = false,
  }) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    const stateBeforeRemove = getState();

    const response = await dispatch(
      removeItems({
        itemIds: [itemId],
        isTradeIn,
        isChildItem,
        isExpressCheckout,
        forceRemove,
      }),
    );

    if (response.type === `${REMOVE_BASKET_ITEMS}.SUCCESS`) {
      if (
        shouldShowCollectionHelpText({
          id: itemId,
          isChildItem,
          state: stateBeforeRemove,
        })
      ) {
        dispatch(triggerAnalyticsEvent(ANALYTICS_SHOW_COLLECTION_HELP_REMOVE));
      }

      const state = getState();
      if (lastItemInBasket) {
        const browseUrl = state?.bff?.browseUrl ?? '';
        const isApp = getIsApplicationSelector(state);
        const processedUrl = getProcessedHandbackUrl(browseUrl, isApp);

        handbackToUrl(undefined, processedUrl);

        if (isApps(state)) {
          dispatch(showEmptyBasketNotification());
        }
      } else {
        await dispatch(getOrderFormData({ isAgeVerification, isCustomerAction: true }));
      }
    }
  };

export const updateItemQuantity =
  ({ itemId = '', quantity = 0 }: { itemId: string; quantity: string | number }) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    const showEditBasketModal = getState()?.delivery?.showEditBasketModal;

    if (showEditBasketModal) {
      const response = await dispatch({
        type: PUT_ITEM_QUANTITY,
        request: (client: AppClient) =>
          client({
            path: URL_PUT_ITEM_QUANTITY(itemId),
            config: {
              method: 'PUT',
              body: { quantity },
            },
          }),
        itemId,
      });

      if (response.type === `${PUT_ITEM_QUANTITY}.SUCCESS`) {
        await dispatch(getOrderFormData({ isCustomerAction: true }));
      }

      if (response.type === `${PUT_ITEM_QUANTITY}.FAILED`) {
        if (response.error) {
          const errorCode = response?.error?.code;

          if (errorCode === errorCodeConstants.ITEM_NOT_IN_STOCK) {
            dispatch({
              type: BASKET_ITEM_OUT_OF_STOCK,
              itemId,
            });
          }

          const getItemsResponse = await dispatch(getOrderFormData({ isCustomerAction: true }));

          if (
            errorCode === errorCodeConstants.ITEM_QUANTITY_PURCHASE_LIMIT ||
            errorCode === errorCodeConstants.QUANTITY_REDUCED_TO_AVAILABLE_STOCK ||
            errorCode === errorCodeConstants.ALL_AVAILABLE_STOCK_ALREADY_ADDED ||
            errorCode === errorCodeConstants.ITEM_QUANTITY_INVALID
          ) {
            const items = getItemsResponse?.result?.orderForm?.items;
            const item = items.find((item: ProductProps) => item.id === itemId);
            const quantity = item?.quantity;
            const formId = getFormId(itemId);

            // ensure field value is updated with correct quantity
            if (quantity) {
              await dispatch(setFieldValue(formId, [ITEM_QUANTITY_INPUT_ID], quantity.toString()));
            }

            const error = getErrorMessageObject({
              error: response.error,
              vars: {
                isItemFabric: isItemFabric(item),
                stockLevel: item?.availability?.stockLevel,
                quantityLimit: item?.availability?.quantityLimit,
              },
            });
            dispatch({
              type: HANDLE_EDIT_BASKET_QUANTITY_ERROR,
              errorMessage: error.message,
              itemId,
            });
          }
        }
      }
    }
  };

export const batchUpdateItemQuantity = (itemsQuantity: { [key: string]: number }) => async (dispatch: AppDispatch) => {
  const response = await dispatch({
    type: PUT_ITEMS_QUANTITIES,
    request: (client: AppClient) =>
      client({
        path: URL_PUT_ITEMS_QUANTITIES,
        config: {
          method: 'PUT',
          body: {
            itemsQuantity,
          },
        },
      }),
  });

  if (response?.type?.includes('SUCCESS')) {
    await dispatch(rebatchOrderAfterBasketEdit());
    await dispatch(getOrderFormData());
    return;
  }

  return response;
};

export const showLimitedStockModal =
  (baseRoutePath = '') =>
  (dispatch: AppDispatch) => {
    dispatch({
      type: SET_EDIT_BASKET_BASE_ROUTE,
      baseRoutePath,
    });
    dispatch(triggerAnalyticsEvent(ANALYTICS_AMEND_BASKET_MODAL_OPEN));
  };

export const showBatchingFailureModal = () => async (dispatch: AppDispatch, getState: AppGetState) => {
  const batchingFailureItems = getState()?.orderForm?.batchingFailureItems ?? [];

  dispatch({
    type: SHOW_BATCHING_FAILURE_MODAL,
  });

  const analyticsProducts = getState()?.analytics?.analyticsData?.checkout?.product ?? [];

  dispatch(
    triggerAnalyticsEvent(ANALYTICS_BATCH_ERROR, {
      products: batchingFailureItems.map((item: ProductProps) =>
        analyticsProducts?.find((product: { productId: string }) => product.productId === item.productId),
      ),
    }),
  );
};

export const removeBatchingFailureItems =
  (items: ProductProps[]) => async (dispatch: AppDispatch, getState: AppGetState) => {
    const itemIds = items.map((item) => item.id as string);
    const removeItemsResponse = await dispatch(removeItems({ itemIds, forceRemove: true, rebatchNotNeeded: true }));

    if (removeItemsResponse.type === `${REMOVE_BASKET_ITEMS}.SUCCESS`) {
      const analyticsProducts = getState()?.analytics?.analyticsData?.checkout?.product ?? [];
      const removedProducts = items.map((item) =>
        analyticsProducts.find((product: { productId: string }) => product.productId === item.productId),
      );

      dispatch(triggerAnalyticsEvent(ANALYTICS_BATCH_ERROR_ITEMS_REMOVED, { products: removedProducts }));
      dispatch(initDeliveryPage({ isDeliveryPageInitialised: true }));
    } else {
      dispatch({ type: SHOW_REMOVE_BATCHING_FAILURE_ERROR });
    }
  };

export const removeItemsFromBasket =
  ({
    itemIds = [],
    shouldInitDeliveryPage = false,
    forceRemove = false,
  }: {
    forceRemove?: boolean;
    itemIds: string[];
    shouldInitDeliveryPage?: boolean;
  }) =>
  async (dispatch: AppDispatch) => {
    await dispatch(removeItems({ itemIds, forceRemove }));
    await dispatch(rebatchOrderAfterBasketEdit({ shouldInitDeliveryPage }));
    await dispatch(getOrderFormData());
  };

export const getIneligibleItemIdsByType = (items: ProductProps[], posCreditIneligibleItemIds: string[]) => {
  const warrantyIds = new Set<string>();
  const otherIds = new Set<string>();

  const childItems = items.flatMap((item) => item.childItems ?? []);
  const itemsById = new Map(items.map((item) => [item.id, item]));
  const childItemsById = new Map(childItems.map((child) => [child.id, child]));

  for (const id of posCreditIneligibleItemIds) {
    const parentItem = itemsById.get(id);

    if (parentItem) {
      if (isWarranty(parentItem)) {
        otherIds.add(id);
        continue;
      }

      const warrantyChildIds = parentItem.childItems?.filter(isWarranty).map((child) => child.id) ?? [];

      if (warrantyChildIds.length > 0) {
        warrantyChildIds.forEach((childId) => warrantyIds.add(childId));
      } else {
        otherIds.add(id);
      }

      continue;
    }

    const childItem = childItemsById.get(id);

    if (childItem && isWarranty(childItem)) {
      warrantyIds.add(id);
    } else {
      otherIds.add(id);
    }
  }

  return {
    warrantyIds: [...warrantyIds],
    otherIds: [...otherIds],
  };
};

export const removePOSCreditIneligibleItems = () => async (dispatch: AppDispatch, getState: AppGetState) => {
  const state = getState();
  const items = getOrderFormItemsSelector(state) ?? [];
  const posCreditIneligibleItemIds = state?.orderForm?.posCreditIneligibleItemIds ?? [];

  const { warrantyIds, otherIds } = getIneligibleItemIdsByType(items, posCreditIneligibleItemIds);

  const respOther = otherIds.length
    ? await dispatch(
        removeItems({
          itemIds: otherIds,
        }),
      )
    : null;

  const respWarranty = warrantyIds.length
    ? await dispatch(
        removeItems({
          itemIds: warrantyIds,
          isChildItem: true,
        }),
      )
    : null;

  const okOther = !respOther || respOther.type === `${REMOVE_BASKET_ITEMS}.SUCCESS`;
  const okWarranty = !respWarranty || respWarranty.type === `${REMOVE_BASKET_ITEMS}.SUCCESS`;

  if (okOther && okWarranty) {
    const address = getState()?.delivery?.confirmedDeliveryAddress;

    await dispatch(rebatchOrder(address));
    await dispatch(getOrderFormData());

    dispatch(
      triggerAnalyticsEvent(ANALYTICS_POS_CREDIT_INELIGIBLE_ITEMS_REMOVED, {
        products: [...otherIds, ...warrantyIds].map((productId) => ({ productId })),
      }),
    );

    dispatch(hidePOSCreditBasketAmendModal());
    const singlePageCheckoutEnabled = isSinglePageCheckoutEnabledFeatureActive(state);

    if (!singlePageCheckoutEnabled) {
      dispatch(navigateTo(routeConstants.DELIVERY));
    }
  }

  return respWarranty ?? respOther;
};

export const closeEditBasketModal = () => async (dispatch: AppDispatch) => {
  dispatch({ type: CLOSE_EDIT_BASKET_MODAL });
};
